import { observable, action, computed } from 'mobx'
import { API, IMultiLocationPrice } from '@getgreenline/homi-shared'
import { uniqBy } from 'lodash'

const printingLocalStorageKey = 'printingSettings'

interface IAveryLayout {
  rows: number
  columns: number
  leftRightMargin: number
  topBottomMargin: number
}

interface IPrintingConfig {
  labelWidth: number
  labelHeight: number
  barcodeHeight: number
  pageWidth: number
  pageHeight: number
  marginTop: number
  marginBottom: number
  // Avery labels require additional properties
  layout: 'flat' | IAveryLayout
  paginationOverride?: number
}

interface Presets {
  [key: string]: IPrintingConfig
}

interface Label {
  title: string | null
  barcode: string | null
  price: number | null
  sku: string | null
  overridePrices: IMultiLocationPrice[] | undefined
}

export const MAX_NUMBER_OF_LABELS = 50

export const presets: Presets = {
  'Zebra ZD410 2" x 1"': {
    pageWidth: 2,
    pageHeight: 1,
    labelHeight: 1,
    labelWidth: 2,
    barcodeHeight: 30,
    marginTop: 0,
    marginBottom: 0,
    layout: 'flat',
  },
  'Zebra LP2824 Plus': {
    pageWidth: 2,
    pageHeight: 1.5,
    labelHeight: 1.5,
    labelWidth: 2,
    barcodeHeight: 70,
    marginTop: 0,
    marginBottom: 0,
    layout: 'flat',
  },
  // Reference: https://www.worldlabel.com/Templates/wl-ol875PDF.htm
  'Avery 5160 (3 x 10)': {
    pageWidth: 8.5,
    pageHeight: 11,
    labelHeight: 1,
    labelWidth: 2.625,
    barcodeHeight: 50,
    marginTop: 0,
    marginBottom: 0,
    layout: {
      rows: 10,
      columns: 3,
      leftRightMargin: 0.21975,
      topBottomMargin: 0.5,
    },
    paginationOverride: 30,
  },
}

export class PrintingStore {
  @observable
  companyId = 0

  @observable
  isLoading = false

  @observable
  showAdvanced = false

  @observable
  preset: string | undefined

  @observable
  location = ''

  @observable
  labelWidth = 0

  @observable
  labelHeight = 0

  @observable
  barcodeHeight = 0

  @observable
  pageWidth = 0

  @observable
  pageHeight = 0

  @observable
  marginTop = 0

  @observable
  marginBottom = 0

  @observable
  layout: 'flat' | IAveryLayout = 'flat'

  @observable
  paginationOverride?: number

  @observable
  displayBarcodeNumber = true

  @observable
  displayBarcode = true

  @observable
  displayPrice = false

  @observable
  products: PrintProduct[] = []

  @observable
  useLegacyPrintingStyles = false

  @observable
  locationId: number | undefined

  @computed
  private get productsWithBarcode() {
    return this.products.filter((product) => product.barcode !== null)
  }

  @computed
  private get arrayOfProductsWithBarcode() {
    const arrayOfProducts: PrintProduct[] = []

    this.productsWithBarcode.forEach((product) => {
      if (!product.quantity || product.quantity < 1) {
        return arrayOfProducts.push(product)
      }

      for (let index = 0; index < product.quantity; index++) {
        arrayOfProducts.push(product)
      }
    })

    return arrayOfProducts
  }

  @computed
  get paginatedProductsWithBarcode() {
    const products = this.arrayOfProductsWithBarcode.slice(
      (this.currentPage - 1) * this.labelsPerPage,
      this.currentPage * this.labelsPerPage,
    )

    return uniqBy(products, 'barcode')
  }

  @computed
  get arrayOfLabels() {
    const arrayOfLabels: Label[] = []

    this.productsWithBarcode.forEach((product) => {
      for (let index = 0; index < product.quantity; index++) {
        arrayOfLabels.push({
          title: product.title,
          barcode: product.barcode,
          price: product.price,
          sku: product.sku,
          overridePrices: product.overridePrices,
        })
      }
    })

    return arrayOfLabels
  }

  @computed
  get paginatedLabels() {
    return this.arrayOfLabels.slice(
      (this.currentPage - 1) * this.labelsPerPage,
      this.currentPage * this.labelsPerPage,
    )
  }

  @computed
  get numberOfPages() {
    return Math.ceil(this.arrayOfProductsWithBarcode.length / this.labelsPerPage)
  }

  @computed
  get labelsPerPage() {
    return this.paginationOverride || MAX_NUMBER_OF_LABELS
  }

  getQuantityOfProductOnPage(barcode: string | null): string {
    const labelsOfProductsOnPreviousPages = this.arrayOfLabels
      .slice(0, (this.currentPage - 1) * this.labelsPerPage)
      .filter((label) => label.barcode === barcode).length
    const labelsOfProductOnCurrentPage = this.paginatedLabels.filter(
      (label) => label.barcode === barcode,
    ).length

    const startIndex = labelsOfProductsOnPreviousPages + 1
    const endIndex = labelsOfProductsOnPreviousPages + labelsOfProductOnCurrentPage

    return `From ${startIndex} to ${endIndex}`
  }

  getRetailPrice(product: Label): number | null {
    const locationId = this.locationId
    let price: number | null = product.price
    const mappedLocationIdsAndPrices = new Map<number, number | null>()
    if (locationId) {
      product.overridePrices?.forEach((overridePrice) => {
        locationId &&
          mappedLocationIdsAndPrices.set(overridePrice.locationId, overridePrice.retailPrice)
      })
      const locationPrice = mappedLocationIdsAndPrices?.get(locationId)

      price = locationPrice || product.price
    }

    return price
  }

  @observable
  currentPage = 1

  @observable
  setCurrentPage(currentPage: number) {
    this.currentPage = currentPage
  }

  @action
  addProduct({
    productId,
    barcode,
    quantity,
    price,
    sku,
    goToLastPage = false,
  }: {
    productId: string | null
    barcode: string | null
    quantity: number
    price: number | null
    sku: string | null
    goToLastPage?: boolean
  }) {
    this.products.push(new PrintProduct(productId, barcode, quantity, price, sku))

    if (goToLastPage) {
      this.setCurrentPage(this.numberOfPages)
    }
  }

  @action
  removeProduct(productBarcode: string | null) {
    this.products = this.products.filter((product) => product.barcode !== productBarcode)

    if (this.paginatedProductsWithBarcode.length === 0) {
      this.setCurrentPage(1)
    }
  }

  @action
  changePreset(preset: string) {
    this.preset = preset
    if (presets[preset]) {
      this.pageWidth = presets[preset].pageWidth
      this.pageHeight = presets[preset].pageHeight
      this.labelHeight = presets[preset].labelHeight
      this.labelWidth = presets[preset].labelWidth
      this.marginTop = presets[preset].marginTop
      this.marginBottom = presets[preset].marginBottom
      this.barcodeHeight = presets[preset].barcodeHeight
      this.layout = presets[preset].layout
      this.paginationOverride = presets[preset].paginationOverride
    }
  }

  @action
  async populate(companyId: number, json: any) {
    this.companyId = companyId
    if (json.location) {
      this.location = json.location
    }
    if (json.labelWidth) {
      this.labelWidth = json.labelWidth
    }
    if (json.labelHeight) {
      this.labelHeight = json.labelHeight
    }
    if (json.pageWidth) {
      this.pageWidth = json.pageWidth
    }
    if (json.pageHeight) {
      this.pageHeight = json.pageHeight
    }
    if (json.marginTop) {
      this.marginTop = json.marginTop
    }
    if (json.marginBottom) {
      this.marginBottom = json.marginBottom
    }
    if (json.displaySku) {
      this.displayBarcodeNumber = !!json.displaySku
    }
    if (json.displayBarcode) {
      this.displayBarcode = !!json.displayBarcode
    }
    if (json.locationId) {
      this.locationId = json.locationId
    }
    const products: any[] = json.products || []
    products.forEach((product) => {
      this.addProduct({
        productId: product.productId,
        barcode: product.barcode,
        quantity: product.quantity,
        price: product.price,
        sku: product.sku,
      })
    })
    this.isLoading = true

    // if (url = const locationOfPO = JSON.parse(window.localStorage.getItem('printing-config') || '')
    // this.setLocationId(locationOfPO.locationId)
    for (const labelProduct of this.products) {
      if (labelProduct.productId) {
        try {
          await API.getProductById(this.companyId, labelProduct.productId).then((product) => {
            let title: string
            if (product.shortName) {
              title = product.shortName
            } else if (product.parentProductName) {
              title = `${product.parentProductName} - ${product.name}`
            } else {
              title = product.name
            }

            labelProduct.productId = product.id
            labelProduct.title = title
            labelProduct.barcode = product.barcode
            labelProduct.price = product.price
            labelProduct.sku = product.sku
            labelProduct.overridePrices = product.overridePrices
          })
        } catch (error) {
          console.log(error)
        }
      }
    }
    this.isLoading = false
  }

  @action
  setDisplayBarcode(value: boolean) {
    this.displayBarcode = value
    this.savePrintingSettings()
  }

  @action
  setDisplayBarcodeNumber(value: boolean) {
    this.displayBarcodeNumber = value
    this.savePrintingSettings()
  }

  @action
  setDisplayPrice(value: boolean) {
    this.displayPrice = value
    this.savePrintingSettings()
  }

  @action
  setLegacyPrintingStyles(value: boolean) {
    this.useLegacyPrintingStyles = value
    this.savePrintingSettings()
  }

  @action
  setLocationId(value: number | undefined) {
    this.locationId = value
  }

  savePrintingSettings() {
    const updateStorage = {
      displayBarcodeNumber: this.displayBarcodeNumber,
      displayPrice: this.displayPrice,
      displayBarcode: this.displayBarcode,
      useLegacyPrintingStyles: this.useLegacyPrintingStyles,
    }
    const stringifiedObject = JSON.stringify(updateStorage)
    window.localStorage.setItem(printingLocalStorageKey, stringifiedObject)
  }

  @action
  restorePrintingSetting() {
    const settingsStr = window.localStorage.getItem(printingLocalStorageKey)
    const parsedSettings = settingsStr ? JSON.parse(settingsStr) : {}
    const settings = {
      displayPrice: this.displayPrice,
      displayBarcode: this.displayBarcode,
      displayBarcodeNumber: this.displayBarcodeNumber,
      useLegacyPrintingStyles: this.useLegacyPrintingStyles,
      ...parsedSettings,
    }

    this.displayPrice = settings.displayPrice
    this.displayBarcodeNumber = settings.displayBarcodeNumber
    this.displayBarcode = settings.displayBarcode
    this.useLegacyPrintingStyles = settings.useLegacyPrintingStyles
  }

  constructor() {
    this.changePreset('Zebra ZD410 2" x 1"')
    this.restorePrintingSetting()
  }
}

class Label {
  @observable
  title: string | null = null

  @observable
  barcode: string | null = null
}

export class PrintProduct extends Label {
  @observable
  productId: string | null

  @observable
  quantity = 1

  @observable
  price: number | null = null

  @observable
  overridePrices: IMultiLocationPrice[] | undefined = []

  @observable
  sku: string | null

  constructor(
    productId: string | null,
    barcode: string | null,
    quantity: number,
    price: number | null,
    sku: string | null,
    overridePrices?: IMultiLocationPrice[],
  ) {
    super()
    this.productId = productId
    this.barcode = barcode
    this.quantity = quantity
    this.price = price
    this.sku = sku
    this.overridePrices = overridePrices
  }
}
